import * as THREE from 'three';
import CameraControls from 'camera-controls';
import { createAccessoryMenu, type AccessoryMenuItem } from '../../ui/accessoryMenu.js';
import type { ViewerTool } from './tool.js';
import type { ViewerContext } from '../context.js';

interface AccessoryInfo { price: string; copy?: string; madeIn: string; material: string }
interface AccessoryItem {
  id: string;
  label: string;
  labelEn: string;
  nodes: string[];
  camera: { position: [number, number, number]; target: [number, number, number] } | null;
  colors?: string[];
  info?: AccessoryInfo;
}
interface AccessoriesJson { items: AccessoryItem[] }

/**
 * 액세서리 도구. 로드된 accessories 그룹의 mesh 를 기본 전부 숨기고, 우측 레일 메뉴
 * (accessoryMenu) 를 세운다. 항목 스위치=개별 장착(toggleItem), 패널 열림/닫힘=카메라만(panelCamera),
 * 항목 행 클릭=카메라 이동(focus). 장착과 줌은 완전히 분리.
 * 매핑 안 된 노드(리어 뱃지 오버레이)는 어떤 경우에도 off.
 * dev 콘솔 훅 __accAll__/__acc__ 은 디버그용으로 유지.
 */
export const accessoryTool: ViewerTool = {
  id: 'accessories',
  enabled: (ctx) => !!ctx.config.accessories,
  async setup(ctx: ViewerContext): Promise<void> {
    const group = ctx.scene.getObjectByName('accessories');
    if (!group) { console.warn('[accessories] group 없음 — 로드 실패?'); return; }

    // 전부 숨김 — 매핑 안 된 노드(리어 뱃지 오버레이)는 항상 off.
    group.traverse((o) => { if (o instanceof THREE.Mesh) o.visible = false; });

    // ── 카메라 3-상태 (base/overview/detail) ──
    // base/overview = finalizeFirstPaint 가 잡아둔 풀카 프레이밍 재사용(캡처).
    // getPosition/getTarget receiveEndValue 기본 true → 전환 목표값(최종 프레이밍) 반환.
    const p0 = ctx.controls.getPosition(new THREE.Vector3());
    const t0 = ctx.controls.getTarget(new THREE.Vector3());
    const basePose: readonly number[] = [p0.x, p0.y, p0.z, t0.x, t0.y, t0.z];
    const baseDist = p0.distanceTo(t0);
    let camGen = 0; // 전환 도중 모드 재변경 시 stale then 무효화

    // 모드별 "포즈 + limits" 동시 세팅. detail 은 minDistance 완화(줌 포즈 도달),
    // base/overview 는 좁은 밴드로 과도한 줌인 제거. 밴드 계수는 실화면 튜닝 대상.
    const applyCameraMode = (mode: 'base' | 'overview' | 'detail', detailPose?: readonly number[]): void => {
      const gen = ++camGen;
      const pose = mode === 'detail' && detailPose ? detailPose : basePose;
      const c = ctx.controls;
      c.mouseButtons.right = CameraControls.ACTION.NONE; // 팬 off (전 모드)
      c.touches.three = CameraControls.ACTION.NONE;
      // 전환 중 완화 → setLookAt 이 어떤 포즈든 clamp 없이 도달
      c.minDistance = 0.05;
      c.maxDistance = Math.max(baseDist * 4, 50);
      c.minPolarAngle = 0;
      c.maxPolarAngle = Math.PI;
      void c.setLookAt(pose[0]!, pose[1]!, pose[2]!, pose[3]!, pose[4]!, pose[5]!, true).then(() => {
        if (gen !== camGen) return; // 도착 전 다른 모드로 바뀜 → 무시
        if (mode === 'detail') {
          const d = Math.hypot(pose[0]! - pose[3]!, pose[1]! - pose[4]!, pose[2]! - pose[5]!);
          c.minDistance = d * 0.6;
          c.maxDistance = d * 1.8;
        } else {
          c.minDistance = baseDist * 0.85;
          c.maxDistance = baseDist * 1.3;
          c.minPolarAngle = THREE.MathUtils.degToRad(45);
          c.maxPolarAngle = THREE.MathUtils.degToRad(89);
        }
      });
      ctx.invalidate();
    };
    applyCameraMode('base'); // 시작 = base. 좁은 밴드 확립(RAV4 자유 줌 제거).

    const res = await fetch(`${import.meta.env.BASE_URL}data/accessories-${ctx.config.id}.json?t=${Date.now()}`);
    const { items } = (await res.json()) as AccessoriesJson;

    // 항목 개별 장착 — 그 항목 nodes 만 표시/숨김. 카메라는 안 건드림(줌·토글 분리).
    const toggleItem = (id: string, on: boolean): void => {
      const item = items.find((i) => i.id === id);
      if (!item) { console.warn(`[accessories] 항목 없음: ${id}`); return; }
      for (const name of item.nodes) {
        const obj = group.getObjectByName(name);
        if (!obj) { console.warn(`[accessories] 노드 못 찾음: ${name}`); continue; }
        obj.traverse((o) => { if (o instanceof THREE.Mesh) o.visible = on; });
      }
      ctx.invalidate();
    };
    // 패널 열림/닫힘 → 카메라만. visibility 는 toggleItem 이 독점.
    const panelCamera = (open: boolean): void => { applyCameraMode(open ? 'overview' : 'base'); };
    const focus = (id: string): void => {
      const item = items.find((i) => i.id === id);
      if (!item?.camera) { console.warn(`[accessories] 카메라 없음: ${id}`); return; }
      const cam = item.camera;
      applyCameraMode('detail', [
        cam.position[0], cam.position[1], cam.position[2],
        cam.target[0], cam.target[1], cam.target[2],
      ]);
    };
    const overview = (): void => { applyCameraMode('overview'); };

    // 우측 레일 메뉴 — 컬러 패널은 paint 도구가 만든 스와치를 이관해 담는다.
    if (ctx.paintPanel) {
      const menuItems: AccessoryMenuItem[] = items.map((i) => ({
        id: i.id, label: i.label, labelEn: i.labelEn, thumb: `assets/accessories/${i.id}.webp`,
        ...(i.info ? { info: i.info } : {}),
      }));
      createAccessoryMenu({
        items: menuItems,
        paintPanelEl: ctx.paintPanel.el,
        onToggle: toggleItem,
        onPanelToggle: panelCamera,
        onFocus: focus,
        onOverview: overview,
      });
    }

    if (import.meta.env.DEV) {
      (window as Window & { __accAll__?: (on: boolean) => void }).__accAll__ = (on: boolean): void => {
        for (const i of items) toggleItem(i.id, on);
      };
      (window as Window & { __acc__?: (id: string) => void }).__acc__ = focus;
      // 검증용: 항목 첫 노드의 mesh 가 하나라도 보이면 true.
      (window as Window & { __accVisible__?: (id: string) => boolean }).__accVisible__ = (id: string): boolean => {
        const item = items.find((i) => i.id === id);
        const first = item?.nodes[0];
        if (!first) return false;
        const obj = group.getObjectByName(first);
        let vis = false;
        obj?.traverse((o) => { if (o instanceof THREE.Mesh && o.visible) vis = true; });
        return vis;
      };
    }
  },
};
